import { html } from "lit";
import { formatHtml } from "@trumpf/ux.common-storybook";
import { StatusType } from "@trumpf/ux.ui-widgets/dist/types/components/status/status-types";
import { AssignmentTypes } from "../types";
import { OverviewHeaderIntroRow } from "../../../partial-templates/overview-header/overview-header.stories";
import { addI18nTranslations } from "../../../../utils";
import { pageStoryDecorator } from "../../../../shared/page";
import { renderPageTemplate } from "../../../../utils";

const completed: StatusType = "MISSION_COMPLETED";
const created: StatusType = "MISSION_CREATED";
const planned: StatusType = "MISSION_PLANNED";
const confirmed: StatusType = "MISSION_CONFIRMED";
const proposed: StatusType = "MISSION_PROPOSED";
const technicianScheduled: StatusType = "MISSION_TECHNICIAN_SCHEDULED";

const missionStatusLabels = {
    [proposed]: "Avisiert",
    [confirmed]: "Bestätigt",
    [completed]: "Abgeschlossen",
    [created]: "Angelegt",
    [planned]: "Einsatz geplant",
    [technicianScheduled]: "Techniker geplant",
};

const assignmentTypeLabels = {
    [AssignmentTypes.Service]: "Reparatur",
    [AssignmentTypes.Maintenance]: "Wartung",
    [AssignmentTypes.ServiceMaintenance]: "Reparatur und Wartung",
};

const Template = ({ url }) => {
    addI18nTranslations({
        namespace: "my-missions",
        language: "de-DE",
        translations: {
            filter: {
                assignmentType: {
                    placeholder: "Einsatztyp",
                    values: assignmentTypeLabels,
                },
                status: {
                    placeholder: "Status",
                    values: missionStatusLabels,
                },
                equipment: {
                    placeholder: "Maschine",
                    searchPlaceholder: "Nach Name oder Nummer suchen",
                },
                today: "Heute",
            },
            content: {
                assignmentType: {
                    column: "Einsatztyp",
                    values: assignmentTypeLabels,
                },
                status: {
                    column: "Status",
                    values: missionStatusLabels,
                },
                date: {
                    column: "Einsatzdatum",
                    dateFormat: "DD.MM.YYYY HH:mm",
                },
                equipmentName: {
                    column: "Maschine & Maschinen-NR. /Software & Produkt-NR.",
                },
                missionNumber: {
                    column: "Einsatznummer",
                },
                detailUrl: {
                    column: "",
                },
                sisCaseId: {
                    column: "Fallnummer",
                },
                filterError: {
                    headline: "Keine Ergebnisse",
                    text:
                        "Es wurden keine Ergebnisse gefunden. Bitte ändern Sie Ihre Filtereinstellungen.",
                },
            },
        },
    });
    return html` <div class="ux-t-container">
            ${OverviewHeaderIntroRow({
                type: "my-missions",
                headline: "My Missions",
                buttonLabel: "To Downloads",
                searchPlaceholder: "Einsatz- oder Fallnummer suchen",
            })}
            <ux-sidebar
                name="filter-sidebar-1"
                close-on-esc-key="true"
                close-on-backdrop-click="true"
                transition="slide"
                alignment="right"
                disable-backdrop="false"
                disable-close-icon="false"
                align-header="false"
                align-footer="false"
                opened="false"
            >
                <ux-sidebar-content class="ux-u-pb-xs ux-u-px-xs">
                    <ux-iws-stateful-filter
                        url="generated-mock-data/mymissions-filter.json"
                        scope="my-missions"
                        filter-column-class="ux-col-12 ux-u-mb-xs"
                        disable-tags
                        date-picker
                        date-format="DD.MM.YYYY"
                        range-start-label="Von"
                        range-end-label="Bis"
                        max-date="2023-12-31"
                        min-date="2023-01-01"
                        debug
                    >
                    </ux-iws-stateful-filter>
                </ux-sidebar-content>
                <ux-sidebar-footer>
                    <ux-button
                        class="ux-u-mb-xxs"
                        variant="primary"
                        class="ux-button--full-width"
                        data-trigger-sidebar="toggle,filter-sidebar-1"
                        >Treffer anzeigen</ux-button
                    >
                    <ux-iws-stateful-filter-remove-button
                        variant="secondary"
                        scope="my-missions"
                    ></ux-iws-stateful-filter-remove-button>
                </ux-sidebar-footer>
            </ux-sidebar>
        </div>
        <div class="ux-t-container-full">
            <div class="ux-row u-background-blue-gradient ux-u-shadow-inset">
                <div class="ux-col-10 ux-u-offset-1 ux-u-mt-m ux-u-mt-lg-l">
                    <ux-iws-stateful-filter-button
                        scope="my-missions"
                        data-trigger-sidebar="toggle,filter-sidebar-1"
                        >Filter</ux-iws-stateful-filter-button
                    >
                </div>
                <ux-iws-stateful-overview
                    url="${url}"
                    scope="my-missions"
                    debug="true"
                    count-result-label="Treffer"
                >
                </ux-iws-stateful-overview>
            </div>
        </div>`;
};

const Empty = ({ url }) => html` <div class="ux-t-container">
        ${OverviewHeaderIntroRow({
            type: "my-missions",
            headline: "My Missions",
            buttonLabel: "To Downloads",
        })}
    </div>
    <div class="ux-t-container-full">
        <div class="ux-row u-background-blue-gradient ux-u-shadow-inset">
            <ux-iws-stateful-overview url="${url}" scope="my-missions">
            </ux-iws-stateful-overview>
        </div>
    </div>`;

export default {
    title: "MyTrumpf/Page Templates/My Missions/Overview",
    decorators: window.top.location.pathname.includes("iframe.html")
        ? [pageStoryDecorator]
        : [],
    parameters: {
        docs: {
            source: {
                transform: formatHtml,
            },
        },
        layout: "fullscreen",
    },
};

export const Default = () =>
    renderPageTemplate(() =>
        Template({
            url: "generated-mock-data/mymissions.json",
        })
    );

export const NoResults = () =>
    renderPageTemplate(() =>
        Empty({
            url: "generated-mock-data/mymissions-empty.json",
        })
    );

export const NoFilteredResults = () =>
    renderPageTemplate(() =>
        Empty({
            url: "generated-mock-data/mymissions-filtered-empty.json",
        })
    );
